<script lang="ts">
    import { createEventDispatcher, type SvelteComponent } from 'svelte';
    import FullscreenModal from '@amp/web-app-components/src/components/Modal/FullscreenModal.svelte';
    import NowPlayingBackdrop from '@amp/web-app-components/src/components/NowPlayingBackdrop/NowPlayingBackdrop.svelte';
    import { focusNodeOnMount } from '@amp/web-app-components/src/actions/focus-node-on-mount';
    import CloseIcon from '@amp/web-app-components/assets/icons/close.svg';
    import { prefersMoreContrast } from '@amp/web-app-components/src/stores/prefers-more-contrast';

    const dispatch = createEventDispatcher();

    /**
     * html element that toggles the full screen modal visibility
     * @type {HTMLElement}
     */
    export let modalTriggerElement: HTMLElement;

    /**
     * a property used to determine if full screen state is active
     * @type {boolean}
     */
    export let isFullScreenActive: boolean;

    /**
     * a property used to determine if text content is showing or not
     * @type {boolean}
     */
    export let isTextVisible: boolean;

    /**
     * url to media artwork.
     * passed to NowPlayingBackdrop component to generate background animation.
     * @type {string | null}
     */
    export let artworkURL: string | null = null;

    /**
     * accessiblity text for FullscreenModal
     * @type {string}
     */
    export let ariaLabel: string;

    /**
     * function used in close button for translations
     * @type {function}
     */
    export let translateFn: (key: string) => string;

    let modalElement: SvelteComponent;

    // Determine height of player for text panel content alignment
    let playerHeight: number;

    const handleCloseModalClick = (e: Event) => {
        modalElement.handleModalClose(e);
        dispatch('closeModal');
    };
</script>

<FullscreenModal
    {modalTriggerElement}
    useAriaModal={false}
    {ariaLabel}
    bind:openModal={isFullScreenActive}
    bind:this={modalElement}
    on:close
>
    <div
        class="desktop-now-playing__container"
        class:is-text-off={!isTextVisible}
        data-testid="desktop-now-playing"
        style="--player-desktop-height: {playerHeight}px"
    >
        {#if !$prefersMoreContrast}
            <NowPlayingBackdrop {artworkURL} --text-border-radius="0" />
        {/if}

        <button
            data-testid="text-fullscreen-modal-close-button"
            class="close-button"
            type="button"
            on:click={handleCloseModalClick}
            aria-label={translateFn('AMP.Shared.AX.Close')}
            use:focusNodeOnMount
        >
            <CloseIcon data-testid="text-fullscreen-modal-close-button-svg" />
        </button>

        <div
            class="player"
            data-testid="player"
            bind:clientHeight={playerHeight}
        >
            <div class="artwork-radiosity-container">
                <div class="artwork-radiosity">
                    {#if $$slots['artwork-radiosity']}
                        <slot name="artwork-radiosity" />
                    {/if}
                </div>
            </div>

            <div class="artwork-container">
                {#if $$slots['artwork']}
                    <slot name="artwork" />
                {/if}
            </div>

            {#if $$slots['metadata']}
                <slot name="metadata" />
            {/if}

            {#if $$slots['playback-controls']}
                <slot name="playback-controls" />
            {/if}
        </div>

        {#if isTextVisible}
            {#if $$slots['text']}
                <div class="text">
                    <slot name="text" />
                </div>
            {/if}
        {/if}

        {#if $$slots['shareplay-toggle']}
            <div class="shareplay-toggle">
                <slot name="shareplay-toggle" />
            </div>
        {/if}

        {#if $$slots['now-playing-toggle']}
            <div class="now-playing-toggle">
                <slot name="now-playing-toggle" />
            </div>
        {/if}
    </div>
</FullscreenModal>

<style lang="scss">
    @use '@amp/web-shared-styles/sasskit-stylekit/ac-sasskit-config';
    @use 'amp/stylekit/core/colors' as *;
    @use 'ac-sasskit/core/viewports' as *;
    @use '@amp/web-shared-styles/app/core/viewports' as *;
    @use '@amp/web-shared-styles/app/core/colors' as *;

    $modal-close: rgba(255, 255, 255, 0.64);
    $modal-close-contrast: rgba(0, 0, 0, 0.76);

    .desktop-now-playing__container {
        padding: 0 10vw;
        width: 100%;
        height: 100%;
        box-sizing: border-box;
        position: relative;
        display: grid;
        grid-template-areas: 'controls text';
        grid-template-rows: auto;
        grid-template-columns: 32vw 40vw;
        justify-content: space-between;
        background-position: center;
        background-size: cover;
        background-color: var(
            --nowPlayingBackdropBG
        ); // Sampled from platter backdrop, with scrims

        @media (prefers-contrast: more) {
            --nowPlayingBackdropBG: var(--pageBG);
        }

        &.is-text-off {
            padding: 0 32vw;
            grid-template-columns: 1fr;
        }
    }

    .close-button {
        position: absolute;
        top: 0;
        margin: 16px 20px 10px;
        width: 18px;
        height: 18px;
        align-self: flex-start;
        fill: var(--systemSecondary-onDark, $modal-close);
        z-index: var(--z-default);

        @media (prefers-contrast: more) {
            fill: var(--systemSecondary, $modal-close-contrast);
        }

        &:focus {
            outline: none;
        }
    }

    .player {
        margin-top: 20px;
        width: 100%;
        height: fit-content;
        max-width: calc(
            0.3 * #{viewport-get-property-for('text-fullscreen:xlarge', content)}
        ); // 768px
        grid-area: controls;
        place-self: center;
        place-items: center;
        display: grid;
        grid-template:
            'artwork' auto
            'metadata' 55px
            'scrubber' 65px
            'controls' 55px
            'volume' 26px;
        z-index: calc(var(--z-default) + 1);
    }

    .artwork-radiosity-container {
        grid-area: artwork;
        width: 100%;
        height: 100%;
        transform: scale(0.92);
        transform-origin: bottom center;
        opacity: 0;

        // Avoid flash of radiosity
        // TODO: rdar://106858703 (Suntory: Lyrics Enhancements - Cross-fade artwork updates)
        --global-transition-duration: 1s; // stylelint-disable-line order/order
        transition: var(--global-transition);

        @supports #{'selector(:has(p))'} {
            &:global(:has(.artwork-component--downloaded)) {
                opacity: 1;
                box-shadow: 0 20px 25px swatch(radiosityShadowColor, light),
                    0 10px 25px swatch(radiosityShadowColor, light);
            }
        }
    }

    .artwork-radiosity {
        width: 100%;
        height: 100%;
        filter: blur(20px) saturate(2);
        opacity: 0.4;
    }

    .artwork-container {
        grid-area: artwork;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 8px;

        // Avoid flash of radiosity box shadow
        // TODO: rdar://106858703 (Suntory: Lyrics Enhancements - Cross-fade artwork updates)
        transition: var(--global-transition);

        @supports #{'selector(:has(p))'} {
            &:global(:has(.artwork-component--downloaded)) {
                box-shadow: 0 4px 10px swatch(radiosityShadowColor, light);
            }
        }

        @media (--text-fullscreen-medium) {
            border-radius: 10px;
        }

        @media (--text-fullscreen-large) {
            border-radius: 12px;
        }

        @media (--text-fullscreen-xlarge) {
            border-radius: 18px;
        }

        :global(.artwork-container) {
            width: inherit;
        }
    }

    .now-playing-toggle {
        position: absolute;
        bottom: 20px;
        inset-inline-end: 20px;
        z-index: var(--z-default);

        :global(button) {
            margin: 0 10px;
        }
    }

    .shareplay-toggle {
        position: absolute;
        bottom: 20px;
        left: 20px;
        z-index: var(--z-default);

        :global(button) {
            margin: 0 10px;
        }
    }

    .text {
        max-width: calc(
            0.38 * #{viewport-get-property-for(
                    'text-fullscreen:xlarge',
                    content
                )}
        ); // 973px
        grid-area: text;
        z-index: var(--z-default);

        // Set height and container blur for text panel
        height: var(--player-desktop-height);
        margin: auto 0;
        display: flex;
        flex-direction: column;
        mask-image: linear-gradient(
            to top,
            rgba(0, 0, 0, 0),
            rgb(0, 0, 0) var(--fade-mask-height, 70px),
            rgb(0, 0, 0)
        );
    }
</style>
